import * as React from "react";
import copy from "copy-to-clipboard";

import { DateLike } from "~/lib/date";
import LegacyDispatcher from "~/lib/legacyDispatcher";

import JobLogGroupStore from "~/stores/JobLogGroupStore";
import JobLogLineStore from "~/stores/JobLogLineStore";
import {
  LogGroupHeader,
  LogGroupName,
  LogGroupTime,
  LogLineNumber,
  LogTimestamp,
  LogCopyPermalink,
} from "~/components/shared/Log";

const getStateFromStores = (id: string) => ({
  highlighted: JobLogLineStore.isHighlighted(id),
  expanded: JobLogGroupStore.isExpanded(id),
});

interface Props extends React.HTMLAttributes<HTMLTableRowElement> {
  id: string;
  name: string;
  number: number;
  deemphasized?: boolean;
  startedAt: DateLike | null | undefined;
  finishedAt: DateLike | null | undefined;
  finished: boolean;
  durationPercentage: number | null | undefined;
  timestampsOn: boolean | null | undefined;
  timestampsInUTC: boolean | null | undefined;
  textContent?: string;
  onTimestampClick: (arg1: React.MouseEvent<HTMLElement>) => void | null | undefined;
  innerRef?: React.Ref<HTMLTableRowElement> | null;
}

type State = {
  highlighted: boolean;
  expanded: boolean;
};

export default class JobLogOutputHeader extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      ...getStateFromStores(props.id),
    };
  }

  // Notify the stores that a highlighted line has been rendered. This gives the
  // stores an opportunity to scroll to this line if they need to.
  dispatchRenderedHighlightAction() {
    if (!Features.RefactorLogScrollBehaviour && this.state.highlighted) {
      // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
      return LegacyDispatcher.emit("job_log_group:highlighted", {
        groupId: this.props.id,
        el: this.props.innerRef,
      });
    }
  }

  componentDidMount() {
    JobLogLineStore.addChangeListener(this.props.id, this.handleStoreChange);
    JobLogGroupStore.addChangeListener(this.props.id, this.handleStoreChange);

    if (!Features.BuildSidebar) {
      this.dispatchRenderedHighlightAction();
    }
  }

  componentWillUnmount() {
    JobLogLineStore.removeChangeListener(this.props.id, this.handleStoreChange);
    JobLogGroupStore.removeChangeListener(this.props.id, this.handleStoreChange);
  }

  componentDidUpdate() {
    if (!Features.BuildSidebar) {
      this.dispatchRenderedHighlightAction();
    }
  }

  handleStoreChange = () => {
    this.setState(getStateFromStores(this.props.id));
  };

  handleToggleHeaderClick = (evt: React.MouseEvent<HTMLTableCellElement>) => {
    evt.preventDefault();

    // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
    return LegacyDispatcher.emit("job_log_group:toggle", {
      groupId: this.props.id,
    });
  };

  handleNumberClick = (evt: React.MouseEvent<HTMLTableCellElement>) => {
    evt.preventDefault();

    // @ts-expect-error - TS2554 - Expected 6 arguments, but got 2.
    return LegacyDispatcher.emit("job_log_line:highlight", {
      lineId: this.props.id,
    });
  };

  handleCopyPermalink = (evt: React.MouseEvent) => {
    // Prevent the line from being unhighlighted
    evt.stopPropagation();

    const url = new URL(window.location.href);

    if (Features.LogViewerNewLineUrlFormat) {
      // Line-only format: #jobId/L{lineNumber}
      const [jobId] = this.props.id.split("/");
      url.hash = `${jobId}/L${this.props.number}`;
    } else {
      // Group-line format: #jobId/{groupNumber}
      url.hash = this.props.id;
    }

    copy(url.toString());
  };

  handleTimestampClick = (evt: React.MouseEvent<HTMLElement>) => {
    evt.preventDefault();

    if (this.props.onTimestampClick) {
      this.props.onTimestampClick(evt);
    }
  };

  render() {
    const {
      deemphasized,
      timestampsInUTC,
      durationPercentage,
      finished,
      finishedAt,
      id,
      name,
      number,
      onTimestampClick,
      startedAt,
      timestampsOn,
      innerRef,
      textContent: _textContent,
      ...props
    } = this.props;

    return (
      <LogGroupHeader
        ref={innerRef}
        highlighted={this.state.highlighted}
        deemphasized={this.props.deemphasized}
        {...props}
      >
        <LogLineNumber
          onClick={this.handleNumberClick}
          number={this.props.number}
          highlighted={this.state.highlighted}
          copyButton={<LogCopyPermalink onClick={this.handleCopyPermalink} />}
        />

        {this.props.timestampsOn && (
          <LogTimestamp
            time={this.props.startedAt}
            utc={this.props.timestampsInUTC}
            onClick={this.handleTimestampClick}
          />
        )}

        <LogGroupName
          onClick={this.handleToggleHeaderClick}
          name={this.props.name}
          expanded={this.state.expanded}
        />

        <LogGroupTime
          onClick={this.handleToggleHeaderClick}
          startedAt={this.props.startedAt}
          finishedAt={this.props.finishedAt}
          finished={this.props.finished}
          durationPercentage={this.props.durationPercentage}
        />
      </LogGroupHeader>
    );
  }
}
